// pages/Entegrasyonlar.js — F8: Entegrasyon ayarları (Kargo / E-Fatura / WhatsApp / PayTR)
//
// 4 kart: her kart ilgili modülün config_key'lerini gösterir + secret maskeli + test butonu.
// Backend: /api/admin/integrations[/:module], PUT /:id, POST /test/:module
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  TextField, Button, Switch, Chip, Stack, Divider, Alert, CircularProgress, Tooltip
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PaymentIcon from "@mui/icons-material/Payment";
import LockIcon from "@mui/icons-material/Lock";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import BoltIcon from "@mui/icons-material/Bolt";
import { getIntegrations, updateIntegration, testIntegration } from "../services/api";
import toast, { Toaster } from "react-hot-toast";

const MODULES = [
  { key: "kargo",    title: "Kargo", icon: <LocalShippingIcon />, color: "#1565C0",
    desc: "Aras / MNG / Yurtiçi entegrasyon ayarları. Anahtarlar girilmediği sürece sistem mock mode'da çalışır." },
  { key: "efatura",  title: "E-Fatura", icon: <ReceiptLongIcon />, color: "#2E7D32",
    desc: "İzibiz veya GIB e-fatura entegrasyonu. Test mode varsayılan açık. BH manuel akışına alternatif." },
  { key: "whatsapp", title: "WhatsApp Business", icon: <WhatsAppIcon />, color: "#25D366",
    desc: "Meta Cloud API. Webhook URL: /api/whatsapp/webhook · Verify token integration ayarındadır." },
  { key: "paytr",    title: "PayTR Sanal POS", icon: <PaymentIcon />, color: "#E67E22",
    desc: "BizimHesap üzerinden başvuru. Onay sonrası 4 alan girilince otomatik aktif olur." }
];

// Config key'lerine kullanıcı dostu etiket
const KEY_LABELS = {
  api_user: "API Kullanıcı", api_password: "API Şifre", api_key: "API Anahtarı",
  customer_code: "Müşteri Kodu", customer_no: "Müşteri No",
  ws_user: "WS Kullanıcı", ws_password: "WS Şifre",
  default_carrier: "Varsayılan Kargo Firması",
  username: "Kullanıcı Adı", password: "Şifre", test_mode: "Test Mode (1=açık)",
  phone_number_id: "Telefon Numara ID", access_token: "Access Token",
  verify_token: "Verify Token", webhook_secret: "Webhook Secret",
  merchant_id: "Mağaza ID", merchant_key: "Mağaza Key", merchant_salt: "Mağaza Salt",
  is_test_mode: "Test Mode (1=test)"
};

function ProviderGroup({ provider, items, onSave, savingMap, revealMap, onToggleReveal }) {
  return (
    <Box mb={2}>
      {provider && (
        <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
          {provider.toUpperCase()}
        </Typography>
      )}
      <Stack spacing={1}>
        {items.map(it => (
          <Stack key={it.id} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
            <Tooltip title={`${it.module} / ${it.provider || '(genel)'} / ${it.config_key}`}>
              <Typography sx={{ minWidth: 180, fontSize: 13 }}>
                {KEY_LABELS[it.config_key] || it.config_key}
                {it.is_secret && <LockIcon sx={{ fontSize: 12, ml: 0.5, verticalAlign: "middle", color: "#888" }} />}
              </Typography>
            </Tooltip>
            <TextField
              size="small"
              fullWidth
              type={it.is_secret && !revealMap[it.id] ? "password" : "text"}
              defaultValue={it.config_value || ""}
              placeholder={it.is_secret && it.has_value ? "(değişmiyor)" : ""}
              inputProps={{ "data-id": it.id, maxLength: 4000 }}
              onBlur={(e) => {
                const v = e.target.value;
                // Maskeli değer (••••) gönderme — kullanıcı dokunmadıysa default göründüğü gibi
                if (it.is_secret && /^•+$/.test(v.trim())) return;
                if (v !== (it.config_value || "")) onSave(it.id, { config_value: v });
              }}
              InputProps={it.is_secret ? {
                endAdornment: (
                  <Button size="small" onClick={() => onToggleReveal(it.id)} sx={{ minWidth: 0, p: 0.5 }}>
                    {revealMap[it.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </Button>
                )
              } : null}
            />
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Switch
                size="small"
                checked={it.is_active}
                onChange={(e) => onSave(it.id, { is_active: e.target.checked })}
              />
              <Chip
                label={it.is_active ? "AKTİF" : "kapalı"}
                size="small"
                color={it.is_active ? "success" : "default"}
                sx={{ fontSize: 10, height: 20 }}
              />
              {savingMap[it.id] && <CircularProgress size={14} />}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function ModuleCard({ mod, items, onSave, savingMap, revealMap, onToggleReveal, onTest, testing, testResult }) {
  // PayTR için sabit yer tutucu (henüz integration_settings'te kaydı yok)
  if (mod.key === "paytr" && items.length === 0) {
    return (
      <Card sx={{ borderTop: `4px solid ${mod.color}` }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Box sx={{ color: mod.color }}>{mod.icon}</Box>
            <Typography variant="h6">{mod.title}</Typography>
            <Chip label="BAŞVURU AŞAMASINDA" size="small" color="warning" sx={{ fontSize: 10 }} />
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={1}>{mod.desc}</Typography>
          <Alert severity="info">
            BizimHesap → Avantajlar → Sanal POS başvurusu yapılıyor. Onaylanınca 4 alan (Mağaza ID, Key, Salt, Test mode) burada girilebilir olacak.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // provider'a göre grupla
  const groups = {};
  for (const it of items) (groups[it.provider || ""] ||= []).push(it);

  return (
    <Card sx={{ borderTop: `4px solid ${mod.color}` }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Box sx={{ color: mod.color }}>{mod.icon}</Box>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>{mod.title}</Typography>
          <Chip
            label={items.some(i => i.has_value && i.is_active) ? "ANAHTAR VAR" : "MOCK"}
            color={items.some(i => i.has_value && i.is_active) ? "success" : "default"}
            size="small"
            sx={{ fontSize: 10 }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>{mod.desc}</Typography>
        <Divider sx={{ mb: 2 }} />
        {Object.entries(groups).map(([prov, list]) => (
          <ProviderGroup
            key={prov}
            provider={prov}
            items={list}
            onSave={onSave}
            savingMap={savingMap}
            revealMap={revealMap}
            onToggleReveal={onToggleReveal}
          />
        ))}
        {testResult[mod.key] && (
          <Alert severity={testResult[mod.key].ready ? "success" : "warning"} sx={{ mt: 1 }}>
            {testResult[mod.key].message}
          </Alert>
        )}
      </CardContent>
      <CardActions>
        <Button
          startIcon={testing[mod.key] ? <CircularProgress size={14} /> : <BoltIcon />}
          onClick={() => onTest(mod.key)}
          disabled={testing[mod.key]}
          size="small"
        >
          Bağlantıyı Test Et
        </Button>
      </CardActions>
    </Card>
  );
}

export default function Entegrasyonlar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMap, setSavingMap] = useState({});
  const [revealMap, setRevealMap] = useState({});
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getIntegrations();
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Yüklenemedi");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (id, payload) => {
    setSavingMap(s => ({ ...s, [id]: true }));
    try {
      await updateIntegration(id, payload);
      toast.success("Kaydedildi");
      // Yerel state'i güncelle (re-fetch yerine)
      setItems(list => list.map(it => it.id === id ? {
        ...it,
        ...(payload.config_value !== undefined ? { config_value: payload.config_value, has_value: !!payload.config_value } : {}),
        ...(payload.is_active !== undefined ? { is_active: payload.is_active } : {})
      } : it));
    } catch (e) {
      toast.error(e?.response?.data?.error || "Kayıt başarısız");
    } finally {
      setSavingMap(s => ({ ...s, [id]: false }));
    }
  };

  const handleTest = async (moduleKey) => {
    setTesting(t => ({ ...t, [moduleKey]: true }));
    setTestResult(r => ({ ...r, [moduleKey]: null }));
    try {
      const r = await testIntegration(moduleKey);
      setTestResult(prev => ({ ...prev, [moduleKey]: r.data }));
    } catch (e) {
      setTestResult(prev => ({ ...prev, [moduleKey]: { ready: false, message: e?.response?.data?.error || "Test başarısız" } }));
    } finally {
      setTesting(t => ({ ...t, [moduleKey]: false }));
    }
  };

  const toggleReveal = (id) => setRevealMap(m => ({ ...m, [id]: !m[id] }));

  const itemsByModule = {};
  for (const it of items) (itemsByModule[it.module] ||= []).push(it);

  return (
    <Box>
      <Toaster />
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>🔌 Entegrasyonlar</Typography>
        <Button onClick={load} size="small" variant="outlined" disabled={loading}>
          {loading ? <CircularProgress size={16} /> : "Yenile"}
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Anahtarlar boş olduğu sürece tüm modüller <strong>mock mode</strong>'dadır — sahte AWB/UUID üretilir, gerçek dış servis çağrısı yapılmaz.
        Anahtar girip "AKTİF" yapınca gerçek API'ye geçilir (henüz alttaki adapter implementasyonları stub seviyesinde, ileri turda tamamlanacak).
      </Alert>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2}>
          {MODULES.map(mod => (
            <Grid item xs={12} md={6} key={mod.key}>
              <ModuleCard
                mod={mod}
                items={itemsByModule[mod.key] || []}
                onSave={handleSave}
                savingMap={savingMap}
                revealMap={revealMap}
                onToggleReveal={toggleReveal}
                onTest={handleTest}
                testing={testing}
                testResult={testResult}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
